import React from 'react';
import Layout from '../components/Layout';
import { useScrollAnimation, useStaggeredAnimation } from '../hooks/useScrollAnimation';
import { Scan, Car, Tag, Tv, ExternalLink, ArrowUpRight } from 'lucide-react';

// Extended portfolio data
const portfolioItems = [
  {
    id: 'scansavvy',
    name: 'ScanSavvy',
    tagline: 'Smart barcode scanning that finds the best price instantly.',
    description: 'ScanSavvy revolutionizes shopping by instantly comparing prices across retailers when you scan any barcode. Our AI-powered platform helps consumers make smarter purchasing decisions and save money on everyday products.',
    category: 'Retail Tech',
    icon: Scan,
    color: '#6366F1',
    features: ['Barcode Scanning', 'Price Comparison', 'Deal Alerts'],
    status: 'Active'
  },
  {
    id: 'fairfare',
    name: 'FairFare',
    tagline: 'Real-time ride comparison across transportation platforms.',
    description: 'FairFare aggregates pricing from all major ride-sharing and transportation services in real-time, ensuring you always get the best rate for your journey. Never overpay for a ride again.',
    category: 'Transportation',
    icon: Car,
    color: '#10B981',
    features: ['Multi-Platform', 'Real-Time Pricing', 'Route Optimization'],
    status: 'Active'
  },
  {
    id: 'akas',
    name: 'AKAS',
    tagline: 'Automatic coupon delivery for the stores you love.',
    description: 'AKAS automatically finds and applies the best coupons and discounts for your favorite stores. Our intelligent system learns your shopping habits and delivers savings directly to you.',
    category: 'E-Commerce',
    icon: Tag,
    color: '#F59E0B',
    features: ['Auto-Apply Coupons', 'Store Tracking', 'Savings Dashboard'],
    status: 'Active'
  },
  {
    id: 'forgetv',
    name: 'ForgeTV',
    tagline: 'AI-powered commercial and video creation platform.',
    description: 'ForgeTV empowers businesses to create professional commercials and video content using advanced AI technology. Transform your ideas into compelling visual stories in minutes.',
    category: 'Digital Media',
    icon: Tv,
    color: '#EF4444',
    features: ['AI Video Generation', 'Template Library', 'Brand Kit'],
    status: 'Active'
  }
];

// Page Header
const PageHeader = () => {
  const [headerRef, isVisible] = useScrollAnimation();

  return (
    <div 
      ref={headerRef}
      className="text-center py-24 md:py-32 px-6 md:px-12"
    >
      <span 
        className={`inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#6366F1] mb-6 transition-all duration-600 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        Our Ventures
      </span>
      <h1 
        className={`text-5xl md:text-7xl font-semibold tracking-tight text-[#1F2933] leading-[1.1] mb-8 transition-all duration-600 delay-100 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        data-testid="portfolio-page-title"
      >
        Portfolio
      </h1>
      <p 
        className={`text-lg md:text-xl text-[#4B5563] max-w-2xl mx-auto transition-all duration-600 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        data-testid="portfolio-page-description"
      >
        A curated collection of companies redefining their industries through practical technology.
      </p>
    </div>
  );
};

// Portfolio Card Component
const PortfolioCard = ({ item, index, isVisible }) => {
  const IconComponent = item.icon;
  const isEven = index % 2 === 0;

  return (
    <div
      className={`group relative bg-white rounded-3xl border border-[#E5E7EB] overflow-hidden transition-all duration-500 hover:shadow-xl hover:border-[#6366F1]/20 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
      style={{ transition: 'opacity 0.7s ease-out, transform 0.7s ease-out, box-shadow 0.3s, border-color 0.3s' }}
      data-testid={`portfolio-detail-card-${item.id}`}
    >
      <div className={`grid md:grid-cols-2 ${isEven ? '' : 'md:direction-rtl'}`}>
        {/* Content Side */}
        <div className={`p-8 md:p-12 flex flex-col justify-center ${isEven ? '' : 'md:order-2'}`}>
          <div className="flex items-center gap-3 mb-6">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
              style={{ backgroundColor: `${item.color}15` }}
            >
              <IconComponent size={24} style={{ color: item.color }} />
            </div>
            <span 
              className="px-3 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: `${item.color}15`, color: item.color }}
            >
              {item.status}
            </span>
          </div>

          <span className="text-xs font-medium uppercase tracking-wider text-[#9CA3AF] block mb-2">
            {item.category}
          </span>

          <h2 className="text-3xl md:text-4xl font-semibold text-[#1F2933] mb-4 group-hover:text-[#6366F1] transition-colors duration-200">
            {item.name}
          </h2>

          <p className="text-lg text-[#4B5563] font-medium mb-4">
            {item.tagline}
          </p>

          <p className="text-[#6B7280] leading-relaxed mb-8">
            {item.description}
          </p>

          {/* Features */}
          <div className="flex flex-wrap gap-2 mb-8">
            {item.features.map((feature, idx) => (
              <span 
                key={idx}
                className="px-4 py-2 bg-[#F9FAFB] rounded-full text-sm text-[#4B5563] border border-[#E5E7EB]"
              >
                {feature}
              </span>
            ))}
          </div>

          {/* CTA */}
          <button 
            className="inline-flex items-center gap-2 text-[#6366F1] font-medium hover:text-[#4F46E5] transition-colors group/btn w-fit"
            data-testid={`learn-more-${item.id}`}
          >
            <span>Learn more</span>
            <ArrowUpRight size={18} className="transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </button>
        </div>

        {/* Visual Side */}
        <div 
          className={`relative min-h-[300px] md:min-h-[400px] ${isEven ? 'md:order-2' : 'md:order-1'}`}
          style={{ backgroundColor: `${item.color}08` }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Abstract visual representation */}
            <div className="relative">
              <div 
                className="w-32 h-32 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3"
                style={{ backgroundColor: `${item.color}20` }}
              >
                <IconComponent size={48} style={{ color: item.color }} />
              </div>
              
              {/* Decorative floating elements */}
              <div 
                className="absolute -top-8 -right-8 w-16 h-16 rounded-full opacity-40 transition-transform duration-500 group-hover:-translate-y-2"
                style={{ backgroundColor: `${item.color}30` }}
              />
              <div 
                className="absolute -bottom-6 -left-6 w-12 h-12 rounded-xl opacity-30 transition-transform duration-500 group-hover:translate-x-2"
                style={{ backgroundColor: `${item.color}30` }}
              />
              <div 
                className="absolute top-1/2 -right-16 w-8 h-8 rounded-full opacity-20 transition-transform duration-500 group-hover:translate-x-2"
                style={{ backgroundColor: item.color }}
              />
            </div>
          </div>
          
          {/* Grid pattern overlay */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(${item.color} 1px, transparent 1px), linear-gradient(90deg, ${item.color} 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
        </div>
      </div>
    </div>
  );
};

// Portfolio Grid
const PortfolioGrid = () => {
  const [containerRef, visibleItems] = useStaggeredAnimation(portfolioItems.length, 200);

  return (
    <section 
      ref={containerRef}
      className="px-6 md:px-12 pb-24 md:pb-32"
      data-testid="portfolio-grid-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="space-y-8 md:space-y-12">
          {portfolioItems.map((item, index) => (
            <PortfolioCard 
              key={item.id} 
              item={item} 
              index={index}
              isVisible={visibleItems.includes(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Main Portfolio Page
const PortfolioPage = () => {
  return (
    <Layout>
      <PageHeader />
      <PortfolioGrid />
    </Layout>
  );
};

export default PortfolioPage;
